import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { problemBankApi } from '@/lib/problemBankApi';
import { ProblemBankEntry } from '@/types/problemBank';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Download,
  CheckCircle,
  AlertTriangle,
  Clock,
  Building2,
  MapPin,
  User,
  Phone,
  Mail,
  FileText,
  Image,
  Video,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Award,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';

const STATUS_COLORS = {
  lead: 'bg-gray-100 text-gray-700 border-gray-200',
  prospect: 'bg-blue-100 text-blue-700 border-blue-200',
  verified: 'bg-green-100 text-green-700 border-green-200',
  assigned: 'bg-purple-100 text-purple-700 border-purple-200',
  solved: 'bg-emerald-100 text-emerald-700 border-emerald-200'
};

export default function AdminProblemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [problem, setProblem] = useState<ProblemBankEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedSquad, setSelectedSquad] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');

  useEffect(() => {
    if (id) {
      loadProblem();
    }
  }, [id]);

  const loadProblem = async () => {
    setIsLoading(true);
    try {
      const data = await problemBankApi.getProblem(id!);
      setProblem(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load problem',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      // await problemBankApi.deleteProblem(id!);
      toast({
        title: 'Success',
        description: 'Problem deleted successfully'
      });
      navigate('/admin/problem-bank');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete problem',
        variant: 'destructive'
      });
    }
  };

  const handleAssign = async () => {
    if (!selectedSquad) return;
    
    try {
      await problemBankApi.updateProblem(id!, {
        status: 'assigned',
        assignment: {
          squadId: selectedSquad,
          assignmentDate: new Date().toISOString(),
          notes: assignmentNotes
        }
      });
      toast({
        title: 'Success',
        description: 'Problem assigned to squad'
      });
      setShowAssignDialog(false);
      loadProblem();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to assign problem',
        variant: 'destructive'
      });
    }
  };

  const handleCalculateScore = async () => {
    try {
      await problemBankApi.calculatePriorityScore(id!);
      toast({
        title: 'Success',
        description: 'Priority score calculated'
      });
      loadProblem();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to calculate score',
        variant: 'destructive'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-96" />
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Problem Not Found</h2>
            <p className="text-muted-foreground mb-4">The problem you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/admin/problem-bank')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Problem Bank
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/problem-bank')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold tracking-tight">{problem.title}</h1>
              <Badge variant="outline" className={STATUS_COLORS[problem.status]}>
                {problem.status.toUpperCase()}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="font-mono">{problem.problemId}</span>
              <span>•</span>
              <span>Discovered: {new Date(problem.dateDiscovered).toLocaleDateString()}</span>
              <span>•</span>
              <span>Last Updated: {new Date(problem.lastUpdated).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/admin/problem/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" onClick={() => setShowAssignDialog(true)}>
            <Users className="h-4 w-4 mr-2" />
            Assign
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button 
            variant="destructive" 
            size="icon"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Priority Banner */}
      {problem.priorityScore && (
        <Card className={`border-2 ${
          problem.priorityScore.priorityTier === 'critical' 
            ? 'border-red-200 bg-red-50' 
            : problem.priorityScore.priorityTier === 'high'
            ? 'border-orange-200 bg-orange-50'
            : 'border-yellow-200 bg-yellow-50'
        }`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {problem.priorityScore.priorityTier === 'critical' ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <TrendingUp className="h-5 w-5 text-orange-600" />
              )}
              <div>
                <p className="font-semibold">
                  {problem.priorityScore.priorityTier.toUpperCase()} PRIORITY
                </p>
                <p className="text-sm text-muted-foreground">
                  Score: {problem.priorityScore.totalScore.toFixed(1)}/5.0
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleCalculateScore}>
              Recalculate Score
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="quantification">Quantification</TabsTrigger>
              <TabsTrigger value="root-cause">Root Cause</TabsTrigger>
              <TabsTrigger value="solution">Solution</TabsTrigger>
              <TabsTrigger value="evidence">Evidence</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Problem Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed">{problem.quantification.description}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Key Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-600 font-medium mb-1">Annual Loss</p>
                      <p className="text-2xl font-bold text-red-700">
                        KES {(problem.quantification.totalLossPerYear / 1000000).toFixed(1)}M
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 font-medium mb-1">Market Size</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {problem.solutionPotential.marketSizeKenya} factories
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-600 font-medium mb-1">Payback</p>
                      <p className="text-2xl font-bold text-green-700">
                        {problem.solutionPotential.anchorPaybackMonths} months
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-purple-600 font-medium mb-1">Dev Cost</p>
                      <p className="text-2xl font-bold text-purple-700">
                        KES {(problem.solutionPotential.estimatedDevCost / 1000)}k
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Anchor Company</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{problem.anchor.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Building2 className="h-4 w-4" />
                          <span>{problem.anchor.location}</span>
                          <span>•</span>
                          <MapPin className="h-4 w-4" />
                          <span>{problem.anchor.plantDepartment}</span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h4 className="font-medium mb-3">Contact Person</h4>
                      <div className="flex items-start gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback>
                            {problem.anchor.contactPerson.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium">{problem.anchor.contactPerson.name}</p>
                          <p className="text-sm text-muted-foreground">{problem.anchor.contactPerson.title}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <a href={`mailto:${problem.anchor.contactPerson.email}`} className="hover:underline">
                                {problem.anchor.contactPerson.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <a href={`tel:${problem.anchor.contactPerson.phone}`} className="hover:underline">
                                {problem.anchor.contactPerson.phone}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Other tabs would be similar to ForgeProblemDetail but with admin controls */}
            <TabsContent value="quantification">
              <Card>
                <CardHeader>
                  <CardTitle>Loss Quantification</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Detailed loss calculation data would appear here.</p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Column - Admin Actions */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Admin Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full justify-start" variant="outline">
                <Edit className="h-4 w-4 mr-2" />
                Edit Problem
              </Button>
              <Button className="w-full justify-start" variant="outline" onClick={() => setShowAssignDialog(true)}>
                <Users className="h-4 w-4 mr-2" />
                Assign to Squad
              </Button>
              <Button className="w-full justify-start" variant="outline" onClick={handleCalculateScore}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Calculate Priority Score
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <Send className="h-4 w-4 mr-2" />
                Notify Squad
              </Button>
              <Button className="w-full justify-start" variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Problem
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Problem Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Status</span>
                  <Badge className={STATUS_COLORS[problem.status]}>
                    {problem.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Confidence Level</span>
                  <Badge variant="outline" className={
                    problem.quantification.confidenceLevel === 'high' 
                      ? 'bg-green-100 text-green-700'
                      : problem.quantification.confidenceLevel === 'medium'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }>
                    {problem.quantification.confidenceLevel}
                  </Badge>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-sm">{new Date(problem.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span className="text-sm">{new Date(problem.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this problem?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The problem will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Problem to Squad</DialogTitle>
            <DialogDescription>
              Select a squad to work on this problem.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Squad</Label>
              <Select value={selectedSquad} onValueChange={setSelectedSquad}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a squad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="squad-1">EcoHeat Solutions</SelectItem>
                  <SelectItem value="squad-2">AgriTech Innovators</SelectItem>
                  <SelectItem value="squad-3">Logistics Optimizers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assignment Notes</Label>
              <Textarea 
                placeholder="Add any notes or instructions for the squad..."
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={!selectedSquad}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}